import { useCallback, useEffect, useState } from "react";
import { HardDrive } from "lucide-react";
import { toast } from "sonner";
import { Cover } from "@/components/Cover";
import { fmtBytes } from "@/components/OneDriveQuota";
import { usageTone } from "@/components/StoragePill";
import { MoveProgress, MoveOutButton, useOnMoveDone } from "@/components/MoveToOneDrive";
import { RepairsPanel } from "@/components/RepairsPanel";
import { api, formatApiError } from "@/lib/api";

const PALETTE = ["bg-amber-500", "bg-sky-600", "bg-emerald-500", "bg-rose-500", "bg-violet-500", "bg-zinc-500"];

const AccountRow = ({ a, color, total, onLimit }) => {
  const pct = a.limit ? Math.min(100, (a.used / a.limit) * 100) : null;
  const setLimit = async () => {
    const cur = a.limit ? +(a.limit / 1073741824).toFixed(2) : "";
    const v = window.prompt(`Storage limit for ${a.name || a.email} in GB (empty = no limit)`, cur);
    if (v === null) return;
    const gb = v.trim() === "" ? 0 : Number(v);
    if (Number.isNaN(gb) || gb < 0) return toast.error("Enter a size in GB");
    const mb = Math.round(gb * 1024);
    if (Math.abs(mb - Math.round((a.limit || 0) / 1048576)) <= 2) return;
    try {
      await api.patch(`/admin/users/${a.id}`, { storage_limit_mb: mb });
      toast.success(gb ? `${a.name || a.email}: limit set to ${gb} GB` : `${a.name || a.email}: no storage limit`);
      onLimit?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <li className="flex flex-wrap items-center gap-3 py-2 text-xs sm:flex-nowrap" data-testid={`storage-account-${a.id}`}>
      <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${color}`} />
      <div className="min-w-0 flex-1">
        <p className="truncate font-medium text-zinc-800">{a.name || a.email}{a.is_you && <span className="ml-1.5 rounded bg-zinc-900 px-1 py-px font-mono text-[9px] text-white">YOU</span>}</p>
        <p className="truncate font-mono text-[10px] text-zinc-400">{a.email} · {a.tracks} songs · {a.albums} albums</p>
      </div>
      <div className="w-full text-left sm:w-36 sm:text-right">
        <p className="font-mono text-zinc-700" data-testid={`storage-account-used-${a.id}`}>{fmtBytes(a.used)}{a.limit ? <span className="text-zinc-400"> / {fmtBytes(a.limit)}</span> : null}</p>
        {pct !== null ? <div className="mt-1 h-1 overflow-hidden rounded-full bg-zinc-100"><div className={`h-full ${pct > 90 ? "bg-red-500" : color}`} style={{ width: `${pct}%` }} /></div>
          : <p className="text-[10px] text-zinc-400">{total ? `${Math.round((a.used / total) * 100)}% of total` : "no limit"}</p>}
      </div>
      {a.role === "guest" && <button onClick={setLimit} data-testid={`storage-set-limit-${a.id}`} title="Set storage limit" className={`rounded-full border px-2 py-0.5 font-mono text-[10px] transition-colors hover:border-zinc-900 ${pct !== null && pct >= 90 ? "border-red-300 text-red-700" : "border-zinc-200 text-zinc-500"}`}>limit</button>}
    </li>
  );
};

export const StorageOverview = () => {
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get("/storage/overview").then((r) => setD(r.data)).catch((e) => toast.error(formatApiError(e))), []);
  useEffect(() => { load(); }, [load]);
  useOnMoveDone(load);
  if (!d) return <div className="mb-8 h-40 animate-pulse rounded-lg border border-zinc-200 bg-white" />;
  const withData = d.accounts.filter((a) => a.used > 0);
  const me = d.accounts.find((a) => a.is_you);
  const { pct } = usageTone(d.total, d.pool_limit);
  return (
    <section className="mb-8 rounded-lg border border-zinc-200 bg-white p-6" data-testid="storage-overview">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-amber-50 text-amber-700"><HardDrive size={18} /></div>
          <div>
            <h2 className="font-medium">Resonance storage</h2>
            <p className="text-xs text-zinc-500"><span className="font-mono font-medium text-zinc-900" data-testid="storage-total">{fmtBytes(d.total)}</span> of {fmtBytes(d.pool_limit)} used across {d.accounts.length} account{d.accounts.length === 1 ? "" : "s"} · {d.tracks} songs</p>
          </div>
        </div>
        <div className="text-right" data-testid="storage-pool">
          <p className={`font-mono text-2xl ${pct >= 90 ? "text-red-600" : pct >= 70 ? "text-amber-600" : "text-zinc-900"}`} data-testid="storage-pool-pct">{pct}%</p>
          <p className="text-[11px] text-zinc-500" data-testid="storage-pool-free">{fmtBytes(Math.max(0, d.pool_limit - d.total))} free</p>
        </div>
        {me?.used > 0 && <MoveOutButton all size="sm" testidPrefix="move-all" />}
      </div>
      <div className="mt-4 flex h-2.5 overflow-hidden rounded-full bg-zinc-100" data-testid="storage-total-bar" title={`${fmtBytes(d.total)} of ${fmtBytes(d.pool_limit)}`}>
        {withData.map((a, i) => <div key={a.id} className={`${PALETTE[i % PALETTE.length]} transition-[width] duration-700`} style={{ width: `${(a.used / d.pool_limit) * 100}%` }} title={`${a.name || a.email}: ${fmtBytes(a.used)}`} />)}
      </div>
      {pct >= 70 && (
        <p className={`mt-2 text-xs ${pct >= 90 ? "text-red-700" : "text-amber-700"}`} data-testid="storage-pool-warning">
          {pct >= 90 ? "Resonance storage is almost full. " : "Resonance storage is filling up. "}
          Uploads stop when the {fmtBytes(d.pool_limit)} shared pool is reached — move albums to OneDrive or remove songs to free space.
        </p>
      )}
      <p className="mt-2 text-[11px] text-zinc-400" data-testid="storage-pool-note">The {fmtBytes(d.pool_limit)} pool is shared by every account; per-account limits below only split it up. OneDrive and Plex libraries don’t count.</p>

      <MoveProgress />
      <div className="mt-6 grid gap-8 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-400">By account</p>
          <ul className="mt-2 max-h-80 divide-y divide-zinc-100 overflow-y-auto pr-1" data-testid="storage-accounts">
            {withData.map((a, i) => <AccountRow key={a.id} a={a} total={d.total} color={PALETTE[i % PALETTE.length]} onLimit={load} />)}
          </ul>
          {withData.length < d.accounts.length && <p className="mt-2 text-[11px] text-zinc-400" data-testid="storage-accounts-empty">{d.accounts.length - withData.length} account{d.accounts.length - withData.length === 1 ? "" : "s"} use no Resonance storage</p>}
        </div>
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-400">Largest albums</p>
          {d.albums.length === 0 ? <p className="mt-3 text-xs text-zinc-500" data-testid="storage-albums-empty">Nothing is stored in Resonance yet. Songs uploaded with the “Resonance storage” destination appear here.</p> : (
            <ul className="mt-2 divide-y divide-zinc-100" data-testid="storage-albums">
              {d.albums.map((al, i) => (
                <li key={al.id} className="flex items-center gap-3 py-2" data-testid={`storage-album-${al.id}`}>
                  <span className="w-5 font-mono text-[10px] text-zinc-400">{i + 1}</span>
                  <Cover albumId={al.id} hasCover={al.has_cover} version={al.cover_version} alt={al.name} className="h-10 w-10 shrink-0" rounded="rounded" iconSize={14} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{al.name}</p>
                    <p className="truncate text-[11px] text-zinc-500">{al.artist}{al.year ? ` · ${al.year}` : ""} · {al.tracks} song{al.tracks === 1 ? "" : "s"} · <span className="font-mono">{al.account}</span></p>
                  </div>
                  <span className="font-mono text-xs text-zinc-700" data-testid={`storage-album-size-${al.id}`}>{fmtBytes(al.bytes)}</span>
                  {al.is_mine && <MoveOutButton albumIds={[al.id]} label="Move" size="sm" testidPrefix={`move-album-${al.id}`} />}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      {d.repairs && <RepairsPanel repairs={d.repairs} onChange={(repairs) => setD((x) => ({ ...x, repairs }))} />}
    </section>
  );
};
